<template>
  <div class="ai-teaching-container">
    <!-- 主内容区域 -->
    <div class="main-content">
      <!-- 顶部按钮组 -->
      <div class="button-group">
        <button v-for="button in mainButtons" :key="button.id" class="main-button"
          :class="{ active: activeMainButton === button.id }" @click="handleMainButtonClick(button.id)">
          {{ button.label }}
        </button>
      </div>

      <!-- 内容区域 -->
      <div class="content-area">
        <!-- 数据表格区域 -->
        <div class="table-content">
          <!-- 搜索和筛选栏 -->
          <div class="filter-bar">
            <div class="search-filters">
              <el-input v-model="searchParams.keyword" placeholder="搜索标题或内容" class="search-input" clearable>
                <template #prefix>
                  <el-icon>
                    <Search />
                  </el-icon>
                </template>
              </el-input>

              <el-select v-model="searchParams.category" placeholder="全部分类" class="category-select" clearable>
                <el-option label="全部状态" value="" />
                <el-option label="第三方媒体制作" value="thirdMedia" />
                <el-option label="自媒体训练" value="selfMedia" />
                <el-option label="B2B训练" value="b2b" />
                <el-option label="智能体官网训练" value="aiAgent" />
              </el-select>

              <el-select v-model="searchParams.status" placeholder="全部状态" class="status-select" clearable>
                <el-option label="全部状态" value="" />
                <el-option label="新增" value="new" />
                <el-option label="待发布" value="pending" />
                <el-option label="已发布" value="published" />
                <el-option label="失败" value="failed" />
              </el-select>

              <el-button type="primary" @click="handleSearch">
                <el-icon>
                  <Search />
                </el-icon>
                搜索
              </el-button>

              <el-button @click="handleReset">
                <el-icon>
                  <Refresh />
                </el-icon>
                重置
              </el-button>
            </div>
          </div>

          <!-- 数据表格 -->
          <el-table :data="tableData" v-loading="loading" class="data-table"
            :header-cell-style="{
              background: 'transparent',
              color: '#e0e6ff',
              borderBottom: '1px solid rgba(255, 112, 72, 0.3)'
            }">
            <el-table-column type="index" label="序号" width="60" />
            <el-table-column prop="title" label="标题" min-width="200">
              <template #default="{ row }">
                <div class="title-content">
                  {{ row.title }}
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="content" label="内容" min-width="250">
              <template #default="{ row }">
                <div class="content-preview">
                  {{ row.content }}
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="spec" label="预览" width="100">
              <template #default="{ row }">
                <el-button size="small" @click="handlePreview(row)" class="preview-btn">
                  浏览文章
                </el-button>
              </template>
            </el-table-column>
            <el-table-column prop="category" label="全部分类" width="140">
              <template #default="{ row }">
                <el-tag effect="dark" class="category-tag">
                  {{ row.category }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="status" label="全部状态" width="100">
              <template #default="{ row }">
                <el-tag :type="getStatusType(row.status)" effect="dark">
                  {{ row.status }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="time" label="时间" width="120" />

          </el-table>

          <!-- 分页 -->
          <div class="pagination">
            <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
              :page-sizes="[10, 20, 50, 100]" :total="pagination.total" 
              layout="total, sizes, prev, pager, next, jumper"
              @size-change="handleSizeChange" @current-change="handleCurrentChange" />
          </div>
        </div>
      </div>
    </div>

    <!-- 预览对话框 -->
    <el-dialog v-model="showPreviewDialog" title="文章预览" width="800px" :close-on-click-modal="false"
      class="preview-dialog">
      <div class="preview-content">
        <h2 class="preview-title">{{ previewData.title }}</h2>
        <div class="preview-meta">
          <span class="meta-item">分类: {{ previewData.category }}</span>
          <span class="meta-item">状态: {{ previewData.status }}</span>
          <span class="meta-item">时间: {{ previewData.time }}</span>
        </div>
        <div class="preview-body">
          <p>{{ previewData.content }}</p>
        </div>
      </div>

      <template #footer>
        <el-button @click="showPreviewDialog = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 编辑对话框 -->
    <el-dialog v-model="showEditDialog" title="编辑文章" width="600px" :close-on-click-modal="false"
      class="edit-dialog">
      <el-form :model="editForm" :rules="editRules" ref="editFormRef" label-width="80px">
        <el-form-item label="标题" prop="title">
          <el-input v-model="editForm.title" placeholder="请输入文章标题" />
        </el-form-item>

        <el-form-item label="内容" prop="content">
          <el-input v-model="editForm.content" type="textarea" placeholder="请输入文章内容" :rows="6" />
        </el-form-item>

        <el-form-item label="分类" prop="category">
          <el-select v-model="editForm.category" placeholder="请选择分类" class="category-select">
            <el-option label="第三方媒体制作" value="第三方媒体制作" />
            <el-option label="自媒体训练" value="自媒体训练" />
            <el-option label="B2B训练" value="B2B训练" />
            <el-option label="智能体官网训练" value="智能体官网训练" />
          </el-select>
        </el-form-item>

        <el-form-item label="状态" prop="status">
          <el-select v-model="editForm.status" placeholder="请选择状态" class="status-select">
            <el-option label="新增" value="新增" />
            <el-option label="待发布" value="待发布" />
            <el-option label="已发布" value="已发布" />
            <el-option label="失败" value="失败" />
          </el-select>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="showEditDialog = false">取消</el-button>
        <el-button type="primary" @click="handleEditSubmit" :loading="editing">
          保存
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search, Refresh } from '@element-plus/icons-vue'

export default {
  name: 'AiTeaching',
  components: {
    Search,
    Refresh
  },
  data() {
    return {
      // 响应式数据
      activeMainButton: 'all',
      loading: false,
      showPreviewDialog: false,
      showEditDialog: false,
      editing: false,
      editFormRef: null,

      // 主按钮配置
      mainButtons: [
        { id: 'all', label: '全部内容' },
        { id: 'thirdMedia', label: '第三方媒体' },
        { id: 'selfMedia', label: '自媒体' },
        { id: 'b2b', label: 'B2B训练' },
        { id: 'aiAgent', label: '智能体官网' }
      ],

      // 搜索参数
      searchParams: {
        keyword: '',
        category: '',
        status: ''
      },

      // 分页数据
      pagination: {
        page: 1,
        pageSize: 10,
        total: 17
      },

      // 预览数据
      previewData: {
        title: '',
        content: '',
        category: '',
        status: '',
        time: ''
      },

      // 编辑表单数据
      editForm: {
        id: null,
        title: '',
        content: '',
        category: '',
        status: ''
      },

      // 编辑表单验证规则
      editRules: {
        title: [
          { required: true, message: '请输入文章标题', trigger: 'blur' }
        ],
        content: [
          { required: true, message: '请输入文章内容', trigger: 'blur' }
        ],
        category: [
          { required: true, message: '请选择分类', trigger: 'change' }
        ],
        status: [
          { required: true, message: '请选择状态', trigger: 'change' }
        ]
      },

      // 表格数据
      tableData: [
        {
          id: 1,
          title: '门窗选购指南：如何挑选和拍摄的图片？...',
          content: '在装修过程中，门窗的设计至关重要。',
          spec: '浏览文章',
          category: '第三方媒体制作',
          status: '新增',
          time: '9小时前'
        },
        {
          id: 2,
          title: '汽车照明（预加工）：如何选择普通之选',
          content: '在商场、购物车、酒店等家具上进行装饰。',
          spec: '浏览文章',
          category: '第三方媒体制作',
          status: '新增',
          time: '9小时前'
        },
        {
          id: 3,
          title: '门窗选购指南：新材料与智能家居的搭配。',
          content: '在现代家居装修中，门窗设计至关重要。',
          spec: '浏览文章',
          category: '第三方媒体制作',
          status: '新增',
          time: '9小时前'
        },
        {
          id: 4,
          title: '门窗生产厂与加工厂选择的配件、钢材配件。',
          content: '在装修过程中，门窗的设计至关重要。',
          spec: '浏览文章',
          category: '第三方媒体制作',
          status: '新增',
          time: '9小时前'
        },
        {
          id: 5,
          title: '智能门窗生产厂的硬件及设备、服务配套。',
          content: '在门窗的设计中，智能门窗设计至关重要。',
          spec: '浏览文章',
          category: '第三方媒体制作',
          status: '新增',
          time: '9小时前'
        },
        {
          id: 6,
          title: '新材料与智能家居的工艺设计至关重要。',
          content: '在装修过程中，门窗的设计至关重要。',
          spec: '浏览文章',
          category: '第三方媒体制作',
          status: '新增',
          time: '9小时前'
        },
        {
          id: 7,
          title: '新材料与智能家居的工艺设计至关重要。',
          content: '在门窗的设计中，新材料与智能家居的工艺设计至关重要。',
          spec: '浏览文章',
          category: '第三方媒体制作',
          status: '新增',
          time: '9小时前'
        },
        {
          id: 8,
          title: '汽车照明（预加工）TOP450品牌形象。',
          content: '在装修过程中，门窗的设计至关重要。',
          spec: '浏览文章',
          category: '第三方媒体制作',
          status: '新增',
          time: '9小时前'
        },
        {
          id: 9,
          title: '门窗加工厂怎么选？查看品牌大牌图',
          content: '在装修过程中，门窗的设计至关重要。',
          spec: '浏览文章',
          category: '第三方媒体制作',
          status: '新增',
          time: '9小时前'
        },
        {
          id: 10,
          title: '门窗生产厂与加工厂的配套之选',
          content: '在装修过程中，门窗的设计至关重要。',
          spec: '浏览文章',
          category: '第三方媒体制作',
          status: '新增',
          time: '9小时前'
        }
      ]
    }
  },
  methods: {
    handleMainButtonClick(buttonId) {
      this.activeMainButton = buttonId
      this.loadTableData()
    },

    handleSearch() {
      this.pagination.page = 1
      this.loadTableData()
    },

    handleReset() {
      this.searchParams = {
        keyword: '',
        category: '',
        status: ''
      }
      this.pagination.page = 1
      this.loadTableData()
    },

    getStatusType(status) {
      const statusMap = {
        '新增': 'warning',
        '待发布': 'info',
        '已发布': 'success',
        '失败': 'danger'
      }
      return statusMap[status] || 'info'
    },

    // 模拟加载表格数据
    async loadTableData() {
      this.loading = true
      try {
        // 模拟API调用延迟
        await new Promise(resolve => setTimeout(resolve, 500))
        console.log(`加载 ${this.activeMainButton} 数据，搜索参数:`, this.searchParams)
      } catch (error) {
        ElMessage.error('加载数据失败')
        console.error('加载数据失败:', error)
      } finally {
        this.loading = false
      }
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.loadTableData()
    },

    handleCurrentChange(page) {
      this.pagination.page = page
      this.loadTableData()
    },

    handlePreview(row) {
      this.previewData = { ...row }
      this.showPreviewDialog = true
    },

    handleEdit(row) {
      this.editForm = { ...row }
      this.showEditDialog = true
      // 清除表单验证状态
      this.$nextTick(() => {
        if (this.$refs.editFormRef) {
          this.$refs.editFormRef.clearValidate()
        }
      })
    },

    async handleEditSubmit() {
      if (!this.$refs.editFormRef) return

      try {
        const valid = await this.$refs.editFormRef.validate()
        if (!valid) return

        this.editing = true

        // 模拟API调用
        await new Promise(resolve => setTimeout(resolve, 1000))

        ElMessage.success('文章编辑成功')
        this.showEditDialog = false
        this.loadTableData()
      } catch (error) {
        ElMessage.error('编辑失败')
        console.error('编辑失败:', error)
      } finally {
        this.editing = false
      }
    },

    async handlePublish(row) {
      try {
        await ElMessageBox.confirm(
          `确定要发布文章 "${row.title}" 吗？`,
          '发布确认',
          {
            confirmButtonText: '确定发布',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        // 模拟发布操作
        await new Promise(resolve => setTimeout(resolve, 800))
        ElMessage.success('文章发布成功')
        this.loadTableData()
        this.showPreviewDialog = false
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('发布失败')
        }
      }
    },

    async handleDelete(row) {
      try {
        await ElMessageBox.confirm(
          `确定要删除文章 "${row.title}" 吗？`,
          '删除确认',
          {
            confirmButtonText: '确定删除',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        // 模拟删除操作
        await new Promise(resolve => setTimeout(resolve, 500))
        ElMessage.success('删除成功')
        this.loadTableData()
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除失败')
        }
      }
    }
  },
  mounted() {
    // 初始化加载数据
    this.loadTableData()
  }
}
</script>

<style scoped lang="scss">
.ai-teaching-container {
  padding: 24px;
  min-height: calc(100vh - 160px);
  background: transparent;
}

.main-content {
  background: hsla(0, 0%, 100%, .1);
  border-radius: 16px;
  padding: 24px;
  backdrop-filter: blur(10px);
}

.button-group {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.main-button {
  padding: 12px 24px;
  background: hsla(0, 0%, 100%, .1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #e0e6ff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #ff7048;
    border-color: #ff7048;
    transform: translateY(-2px);
  }

  &.active {
    background: #ff7048;
    border-color: #ff7048;
  }
}

.content-area {
  min-height: 400px;
}

.table-content {
  background: hsla(0, 0%, 100%, .1);
  border-radius: 8px;
  padding: 20px;
}

.filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 16px;

  .search-filters {
    display: flex;
    gap: 12px;
    align-items: center;
    flex-wrap: wrap;

    .search-input {
      width: 200px;
    }

    .category-select,
    .status-select {
      width: 150px;
    }
  }
}

.data-table {
  background: transparent;

  :deep(.el-table) {
    background: transparent;

    th {
      background: transparent !important;
      color: #e0e6ff !important;
      border-bottom: 1px solid rgba(255, 112, 72, 0.3) !important;
    }

    tr {
      background: rgba(255, 255, 255, 0.05);

      &:hover {
        background: rgba(255, 255, 255, 0.1) !important;
      }
    }
  }

  .title-content {
    color: #e0e6ff;
    font-weight: 500;
    line-height: 1.4;
  }

  .content-preview {
    color: #a0a8b8;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .preview-btn {
    background: rgba(255, 112, 72, 0.2);
    border: 1px solid rgba(255, 112, 72, 0.3);
    color: #ff7048;

    &:hover {
      background: #ff7048;
      border-color: #ff7048;
      color: #fff;
    }
  }

  .category-tag {
    background: rgba(255, 112, 72, 0.2);
    border: 1px solid rgba(255, 112, 72, 0.3);
    color: #ff7048;
  }

  .action-btn {
    margin: 2px;
  }
}

.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding: 20px 0;
}

// 预览对话框样式
.preview-content {
  .preview-title {
    color: #e0e6ff;
    font-size: 24px;
    font-weight: 600;
    margin-bottom: 16px;
    text-align: center;
  }

  .preview-meta {
    display: flex;
    gap: 20px;
    margin-bottom: 24px;
    padding: 16px;
    background: rgba(255, 112, 72, 0.1);
    border-radius: 6px;
    border: 1px solid rgba(255, 112, 72, 0.2);

    .meta-item {
      color: #e0e6ff;
      font-size: 14px;
    }
  }

  .preview-body {
    color: #a0a8b8;
    line-height: 1.8;
    font-size: 16px;

    p {
      margin: 0;
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .ai-teaching-container {
    padding: 16px;
  }

  .button-group {
    gap: 12px;

    .main-button {
      padding: 10px 20px;
      font-size: 13px;
    }
  }

  .filter-bar {
    flex-direction: column;
    align-items: stretch;

    .search-filters {
      justify-content: center;

      .search-input,
      .category-select,
      .status-select {
        width: 100%;
        max-width: 200px;
      }
    }
  }
}

@media (max-width: 480px) {
  .main-content {
    padding: 16px;
  }

  .button-group {
    justify-content: center;
  }
}
</style>

<style>
/* 全局样式 - 修复对话框问题 */
.preview-dialog .el-dialog,
.edit-dialog .el-dialog {
  background: rgba(13, 5, 26, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 112, 72, 0.3);
  border-radius: 16px;
}

.preview-dialog .el-dialog__header,
.edit-dialog .el-dialog__header {
  margin: 0 !important;
  padding: 20px !important;
}

.preview-dialog .el-dialog__title,
.edit-dialog .el-dialog__title {
  color: #ffffff !important;
  font-weight: 600;
  font-size: 18px;
}

.preview-dialog .el-dialog__body,
.edit-dialog .el-dialog__body {
  padding: 20px !important;
  background: transparent !important;
}

.preview-dialog .el-dialog__footer,
.edit-dialog .el-dialog__footer {
  border-top: 1px solid rgba(255, 112, 72, 0.2) !important;
  padding: 16px 20px !important;
  margin: 0 !important;
}

/* 表单标签颜色 */
.edit-dialog .el-form-item__label {
  color: #e0e6ff !important;
  font-weight: 500;
}

/* 输入框文字颜色 */
.edit-dialog .el-input__inner,
.edit-dialog .el-textarea__inner {
  color: #ffffff !important;
}

.edit-dialog .el-input__inner::placeholder,
.edit-dialog .el-textarea__inner::placeholder {
  color: rgba(255, 255, 255, 0.5) !important;
}

/* 输入框聚焦状态 */
.edit-dialog .el-input__wrapper {
  background: rgba(255, 255, 255, 0.08) !important;
  border: 1px solid rgba(255, 112, 72, 0.3) !important;
  box-shadow: none !important;
}

.edit-dialog .el-input__wrapper:hover {
  border-color: #ff7048 !important;
}

.edit-dialog .el-input__wrapper.is-focus {
  border-color: #ff7048 !important;
  box-shadow: 0 0 0 1px #ff7048 !important;
}

/* 选择器样式 */
.edit-dialog .el-select .el-input__inner {
  color: #ffffff !important;
}

.edit-dialog .el-select .el-select__caret {
  color: #ff7048 !important;
}

/* 按钮样式 */
.preview-dialog .el-button,
.edit-dialog .el-button {
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.preview-dialog .el-button--primary,
.edit-dialog .el-button--primary {
  background: #ff7048 !important;
  border-color: #ff7048 !important;
}

.preview-dialog .el-button--primary:hover,
.edit-dialog .el-button--primary:hover {
  background: #e55a3a !important;
  border-color: #e55a3a !important;
  transform: translateY(-1px);
}

/* 表格样式覆盖 */
.el-table {
  --el-table-bg-color: transparent !important;
  --el-table-tr-bg-color: transparent !important;
  --el-table-header-bg-color: transparent !important;
  --el-table-row-hover-bg-color: rgba(255, 255, 255, 0.05) !important;
  --el-table-current-row-bg-color: rgba(255, 112, 72, 0.1) !important;
  --el-table-header-text-color: #e0e6ff !important;
  --el-table-text-color: #e0e6ff !important;
  --el-table-border-color: rgba(255, 112, 72, 0.3) !important;
}

.el-table th,
.el-table tr,
.el-table td {
  background: transparent !important;
  color: #e0e6ff !important;
  border-bottom: 1px solid rgba(255, 112, 72, 0.1) !important;
}

.el-table th {
  border-bottom: 1px solid rgba(255, 112, 72, 0.3) !important;
}

.el-table .el-table__inner-wrapper::before {
  background-color: rgba(255, 112, 72, 0.3) !important;
}
</style>